import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Typography, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import * as HrActions from '../../../store/actions/hr.action';
import { getUser } from '../../../store/selector/auth.selector';
import { getHrState } from '../../../store/selector/hr.selector';
import { hrcolumns } from './hr-clearance-form-column';
import { hrbpcolumns } from './hrbp-clearance-form-column';
import SignatoryAdd from '../../../shared/components/ui/modal/signatory-add-modal';
import DynamicCrumbs from '../../../shared/components/ui/dashboard-layout/dynamic-crumbs';
import DynamicDataGrid from '../../../shared/components/ui/datagrid/datagrid';
import DynamicTabLink from '../../../shared/components/ui/dynamic-tabs/dynamic-tab-link';
import OptionsMenu from '../../../shared/components/ui/menu/options-menu';
import { IDynamicTabLink } from '../../../shared/interfaces/dynamic-tab.interface';
import { IOptionsButton } from '../../../shared/interfaces/options.interface';
import { IHrApprovalsAddPayload } from '../../../shared/interfaces/hr.interface';
import { hrClearanceFormOptions } from '../../../shared/constants/hr-clearance-form-options';
import { HR_TABS_CONSTANT } from '../../../shared/constants/hr-tabs.constant';
import { HR_VIEW_ONLY_TABS_CONSTANT } from '../../../shared/constants/hr-view-only.constant';
import { HR_BP_TABS_CONSTANT } from '../../../shared/constants/hrbp-tabs.constant';

const styles = {
  header: {
    display: 'flex',
    flexDirection: { sm: 'row', xs: 'column' },
    justifyContent: 'space-between',
    alignItems: { sm: 'center', xs: 'flex-start' },
    mb: 3,
  },

  downloadOptionsButton: {
    padding: '0.5rem 1.5rem',
    borderRadius: '0.5rem',
    width: { sm: 'auto', xs: '100%' },
    mt: { sm: 0, xs: '2rem' },
  },
};

const HrClearanceForm = () => {
  const dispatch = useDispatch();
  const { slug } = useParams<string>();
  /* eslint-disable camelcase */
  const {
    selected: { application },
  } = useSelector(getHrState);

  const user = useSelector(getUser);

  const [tabItems, setTabItems] = useState<IDynamicTabLink[]>();
  const [clearanceForm, setClearanceForm] = useState<any>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<Array<IOptionsButton> | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [params, setParams] = useState<IHrApprovalsAddPayload>({
    application_slug: '',
    signatories: '',
  });

  useEffect(() => {
    const { groups } = user;

    switch (true) {
      case groups.includes('hr-bp'):
        setTabItems(HR_BP_TABS_CONSTANT);
        break;

      case groups.includes('hr-view-only'):
        setTabItems(HR_VIEW_ONLY_TABS_CONSTANT);
        break;

      default:
        setTabItems(HR_TABS_CONSTANT);
        break;
    }
  }, [user]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickHandler = () => {
    setModalOpen(true);
  };

  const onModalAction = async (e: string, signatory?: Array<string>) => {
    if (e === 'confirm') {
      setParams((prev: IHrApprovalsAddPayload) => {
        return {
          ...prev,
          application_slug: slug,
          signatories: JSON.stringify(signatory),
        };
      });

      setModalOpen(false);
    } else {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    onAddApprovals();
  }, [params]);

  const onAddApprovals = async () => {
    const { signatories } = params;

    if (signatories.length > 0) dispatch(HrActions.HrAddApprover(params));
  };

  useEffect(() => {
    if (slug) dispatch(HrActions.HrFetchApplication(slug));
  }, []);

  useEffect(() => {
    if (application?.status === 'finalized') {
      setOptions(hrClearanceFormOptions('finalized', onClickHandler, slug));
    } else {
      if (user.groups.includes('hr-bp')) {
        setOptions(hrClearanceFormOptions('hrbp', onClickHandler, slug));
      } else {
        setOptions(hrClearanceFormOptions('other', onClickHandler, slug));
      }
    }
  }, []);

  useEffect(() => {
    if (application) {
      const supervisorClearance = {
        approved_at: application.approved_at,
        company: application.company,
        created_at: application.created_at,
        day_processed: application.day_processed,
        department: application.supervisor_department,
        position: application.supervisor_position,
        remarks: application.supervisor_remarks,
        signatory: application.supervisor,
        signatory_email: application.supervisor_email_address,
        slug: 'immediateSupervisor',
        status: application.status,
        updated_at: application.updated_at,
      };
      const clearance = [supervisorClearance, ...application.signatory_remarks];
      setClearanceForm(clearance);
    }
  }, [application]);

  return (
    <>
      <Box sx={styles.header}>
        <Box>
          <DynamicCrumbs />

          <Typography sx={{ fontSize: '1.5rem' }}>
            {application?.lastname}, {application?.firstname}, {application?.middlename} -{' '}
            {application?.code}
          </Typography>
        </Box>

        <Button
          variant='contained'
          sx={styles.downloadOptionsButton}
          disableElevation
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
          fullWidth
        >
          Actions
        </Button>

        <OptionsMenu
          open={open}
          handleClose={handleClose}
          anchorEl={anchorEl}
          optionItems={options}
        />
      </Box>
      {tabItems && <DynamicTabLink tabs={tabItems} />}

      <Box sx={{ my: 2, background: '#fff', height: '80vh', width: '100%' }}>
        {clearanceForm && (
          <DynamicDataGrid
            columns={
              user.groups.includes('hr-bp') || user.groups.includes('hr-view-only')
                ? hrbpcolumns
                : hrcolumns
            }
            rows={clearanceForm}
          />
        )}
      </Box>

      {application && (
        <SignatoryAdd
          isModalOpen={modalOpen}
          params={{ province: application.province, step: application.step }}
          onClickModal={(e: string, signatories?: string[]) => onModalAction(e, signatories)}
        />
      )}
    </>
  );
};
export default HrClearanceForm;
